// @ts-check
import React, { StatelessComponent } from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import cx from 'classnames';
import LoadingContent from 'shared/components/LoadingContent';
import Icon from 'shared/components/Icon';
import _ from 'lodash';

/** @type { StatelessComponent<{helpTipId: string}> } */
const HelpTipCircle = ({ helpTipId }) => (
  <span className="HelpTipCircle">
    <a data-tip data-for={helpTipId}>
      ?
    </a>
  </span>
);

HelpTipCircle.displayName = 'HelpTipCircle';

HelpTipCircle.propTypes = {
  helpTipId: PropTypes.string.isRequired,
};

const childrenRenderer = children => (_.isFunction(children) ? children() : children);

/** @typedef {{
  id?: string,
  span: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
  icon?: string,
  title: string,
  extraTitle?: JSX.Element,
  isLoading?: boolean,
  helpTipId?: string,
  children: JSX.Element | JSX.Element[],
  className?: string,
  nopadding?: boolean
}} WidgetPropsType */

/** @type { StatelessComponent<WidgetPropsType> } */
const Widget = ({
  id,
  span,
  icon,
  title,
  extraTitle,
  isLoading,
  helpTipId,
  children,
  className,
  nopadding,
}) => {
  const contentClasses = cx('widget-content', className, { nopadding: nopadding || isLoading });

  return (
    <div className={`span${span}`} id={id}>
      <div className="widget-box">
        <div className="widget-title">
          <span className="icon">
            <Icon name={icon} />
          </span>
          {title ? <h5>{title}</h5> : null}
          {extraTitle}
          {helpTipId ? <HelpTipCircle helpTipId={helpTipId} /> : null}
        </div>
        <div className={contentClasses}>
          {isLoading ? <LoadingContent /> : childrenRenderer(children)}
        </div>
      </div>
    </div>
  );
};

Widget.displayName = 'Widget';

Widget.propTypes = {
  span: PropTypes.number.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  extraTitle: PropTypes.node,
  isLoading: PropTypes.bool.isRequired,
  helpTipId: PropTypes.string,
  className: PropTypes.string,
  nopadding: PropTypes.bool.isRequired,
};

Widget.defaultProps = {
  span: 8,
  icon: 'pencil',
  title: '',
  nopadding: false,
  isLoading: false,
  helpTipId: undefined,
  className: '',
};

export default Widget;
